import { Injectable } from '@angular/core';

import { paragraphDTO, Sentences, sentenceDTO, spellingStateEnum, tokenDTO } from '@capiche/ts-data-models-shared';

import { CapicheLoggerService } from 'capiche-logger';
import { CapicheMixpanelService } from 'capiche-mixpanel';

import { TextProcessorService } from '../text-processor/text-processor.service';
import { ProcessingPipelineService } from '../processing-pipeline/processing-pipeline.service';

@Injectable({
  providedIn: 'root',
})
export class ReplacementService {
  logger = new CapicheLoggerService('replacement.service:', false);

  constructor(
    private textProcessor: TextProcessorService,
    private mixpanel: CapicheMixpanelService,
    private pipeline: ProcessingPipelineService,
  ) {}

  /**
   * Replace a single misspelled token.
   *
   * Callers pass positional `.index` values (absolute/sentence-local character
   * positions) that uniquely identify the target even when identical text appears
   * multiple times in the document.  This service resolves the full DTO path
   * internally from the pipeline's per-element document store — callers need not
   * hold paragraph references.
   *
   * `elId` identifies the editor instance; web-app callers pass `PROSEMIRROR_ELEMENT_ID`,
   * the Chrome extension passes its per-element `capiche-<uuid>` id, and UI library
   * components receive it as an `@Input`.  (CSW-1453: resolution moved from the
   * ProseMirror-hardwired DocumentStateService to pipeline.getDoc(elId) so the
   * service works for any editor surface.)
   */
  public makeReplacement(
    elId: string,
    paragraphIndex: number,
    sentenceIndex: number,
    tokenIndex: number,
    chosenReplacement: string,
  ): void {
    this.logger.log('makeReplacement:', { elId, paragraphIndex, sentenceIndex, tokenIndex, chosenReplacement });

    const docState: paragraphDTO[] = this.pipeline.getDoc(elId)?.paragraphs ?? [];
    const para: paragraphDTO | undefined = docState.find((p: paragraphDTO) => p.index === paragraphIndex);

    if (!para?.srcId) {
      this.logger.warn('makeReplacement: paragraph not found or has no srcId', { paragraphIndex });
      return;
    }

    const sentOrdinal: number = para.sentences.findIndex((s: sentenceDTO) => s.index === sentenceIndex);
    const sent: sentenceDTO | undefined = sentOrdinal >= 0 ? para.sentences[sentOrdinal] : undefined;
    if (!sent) {
      this.logger.warn('makeReplacement: sentence not found', { sentenceIndex });
      return;
    }

    const tokOrdinal: number = sent.tokens.findIndex((t: tokenDTO) => t.index === tokenIndex);
    const tok: tokenDTO | undefined = tokOrdinal >= 0 ? sent.tokens[tokOrdinal] : undefined;
    if (!tok) {
      this.logger.warn('makeReplacement: token not found', { tokenIndex });
      return;
    }

    this.pipeline.requestReplacement(elId, para.srcId, sentOrdinal, tokOrdinal, chosenReplacement);
    this.storeReplacement(tok, chosenReplacement);
  }

  /**
   * Replace an entire sentence with its LLM suggestion.
   *
   * Resolves the sentence internally from the pipeline's per-element document
   * store — callers pass positional indices only.  See `makeReplacement` for the
   * identity model.
   */
  public makeSentenceReplacement(elId: string, paragraphIndex: number, sentenceIndex: number): void {
    this.logger.log('makeSentenceReplacement:', { elId, paragraphIndex, sentenceIndex });

    const docState: paragraphDTO[] = this.pipeline.getDoc(elId)?.paragraphs ?? [];
    const para: paragraphDTO | undefined = docState.find((p: paragraphDTO) => p.index === paragraphIndex);

    if (!para?.srcId) {
      this.logger.warn('makeSentenceReplacement: paragraph not found or has no srcId', { paragraphIndex });
      return;
    }

    const sentOrdinal: number = para.sentences.findIndex((s: sentenceDTO) => s.index === sentenceIndex);
    const sent: sentenceDTO | undefined = sentOrdinal >= 0 ? para.sentences[sentOrdinal] : undefined;
    if (!sent) {
      this.logger.warn('makeSentenceReplacement: sentence not found', { sentenceIndex });
      return;
    }

    this.pipeline.requestSentenceReplacement(elId, para.srcId, sentOrdinal, sent.suggestion);

    // Persist replacements for misspelled/contextual tokens only.
    const replacementWords: string[] = Sentences.fromString(sent.suggestion)
      .toDTOs()
      .flatMap((s: sentenceDTO) => s.tokens.map((t: tokenDTO) => t.input));

    sent.tokens.forEach((token: tokenDTO, i: number) => {
      if (token.spellingState === spellingStateEnum.misspelled || token.spellingState === spellingStateEnum.contextual) {
        this.storeReplacement(token, replacementWords[i]);
      }
    });
  }

  private async storeReplacement(wordReplacement: tokenDTO, chosenSuggestion: string): Promise<void> {
    const response = await this.textProcessor.replaceWord({
      word: wordReplacement,
      suggestion: chosenSuggestion,
    });
    this.mixpanel.track('Replacement', {
      replacedToken: JSON.stringify(wordReplacement),
      chosenSuggestion: chosenSuggestion,
    });
    this.logger.log('storeReplacement: response:', response);
  }

}
